import { useMemo, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { offset } from '@floating-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/* CSS */
import styles from './TripHeader.module.css';
import shared from '../../styles/shared.module.css';

/* Icons */
import edit_icon from '../../assets/icons/edit_icon.svg';
import chevron_down from '../../assets/icons/chevron_down.svg';
import calendar_icon from '../../assets/icons/calendar_icon.svg';
import tick_icon_white from '../../assets/icons/tick_icon_white.svg';

/* Utils */
import { createMaskedDisplay, extractDigits, buildDateFromDigits, formatDigits, validateDates, calculateDurationFromDates, digitsToCursorPos, slotToMaskPos, maskPosToSlotForward, maskPosToSlotBackward } from '../../utils/dateUtils';
import { COUNTRIES } from '../../utils/countryUtils';
import { tripUtils } from '../../utils/tripUtils';

/* Context */
import { useTripContext } from './TripContext';
import { useToast } from '../../context/ToastContext';

/* Hooks */
import { useReadOnlyToast } from '../../hooks/useReadOnlyToast';

const TripHeader = ({
    isEditingTitle, setIsEditingTitle,
    titleError, setTitleError,
    missingDestination, setMissingDestination,
    missingFromDate, setMissingFromDate,
    missingToDate, setMissingToDate,
    titleInputRef, titleSectionRef,
    destinationInputRef, fromDateInputRef, toDateInputRef,
    destinationWrapperRef
}) => {

    // Grab global data from context
    const {
        title, setTitle,
        selectedCountries, setSelectedCountries,
        fromDateObj, setFromDateObj,
        toDateObj, setToDateObj,
        durationInDays, setDurationInDays,
        canEdit,
        setIsDirty
    } = useTripContext();

    // Debounce components
    const { id: tripId } = useParams();
    const titleTimer = useRef(null);
    const { showToast } = useToast();
    const notifyReadOnly = useReadOnlyToast();

    // Local UI state (Dropdowns, Animations, String buffers)
    const [countryQuery, setCountryQuery] = useState('');
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [fromDateSlots, setFromDateSlots] = useState('________');
    const [toDateSlots, setToDateSlots] = useState('________');
    const [fromDateError, setFromDateError] = useState(false);
    const [toDateError, setToDateError] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Filtered countries based on search query
    const filteredCountries = useMemo(() => {
        const q = countryQuery.toLowerCase();
        if (!q) return COUNTRIES;
        return COUNTRIES.filter(country =>
            country.toLowerCase().includes(q));
    }, [countryQuery]);

    // Format the destination display
    const destinationDisplay =
        selectedCountries.length === 0
            ? ""
            : selectedCountries.join(' · ');

    // Start editing the title
    const startEditingTitle = () => {
        if (!canEdit) return notifyReadOnly();
        setIsEditingTitle(true);
        setTitleError(false);

        // Focus and select the input after state update
        requestAnimationFrame(() => {
            if (titleInputRef.current) {
                titleInputRef.current.focus();
                titleInputRef.current.select();
            };
        });
    };

    // Enter or Escape to blur the title input cursor
    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
            e.target.blur();
        }
    };

    // Open the dropdown and focus input
    const openCountryDropdown = () => {
        if (!canEdit) return notifyReadOnly();
        setIsCountryDropdownOpen(true);
        setCountryQuery('');

        if (destinationInputRef.current) {
            destinationInputRef.current.focus();
        }
    }

    // Handle country selection (toggle)
    const handleSelectCountry = (country) => {
        if (!canEdit) return notifyReadOnly();
        setIsDirty(true);
        const updated = selectedCountries.includes(country)
            ? selectedCountries.filter(c => c !== country)
            : [...selectedCountries, country];

        setSelectedCountries(updated);
        if (updated.length > 0) setMissingDestination(false);

        if (tripId) {
            tripUtils.updateTripMeta(tripId, { locations: updated })
                .catch(() => showToast('Failed to save destinations', 'error'));
        }
    };

    // Handle user changing title input
    const handleTitleChange = (e) => {
        if (!canEdit) return notifyReadOnly();
        const value = e.target.value;
        setTitle(value);
        setIsDirty(true);
        if (value.trim()) setTitleError(false);

        if (tripId) {
            clearTimeout(titleTimer.current);
            titleTimer.current = setTimeout(() => {
                tripUtils.updateTripMeta(tripId, { title: value })
                    .catch(() => showToast('Failed to save title', 'error'));
            }, 600);
        }
    };


    // Keystroke handler — cursor-aware insert/delete at any position
    const handleFromDateKeyDown = (e) => {
        if (!canEdit) return notifyReadOnly();
        const input = fromDateInputRef.current;
        if (!input) return;
        const { key, ctrlKey, metaKey } = e;
        if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) return;
        if (ctrlKey || metaKey) return;
        e.preventDefault();

        const cursorPos = input.selectionStart;
        let slots = fromDateSlots;
        let newCursorPos = cursorPos;

        if (/^[0-9]$/.test(key)) {
            const slotIdx = maskPosToSlotForward(cursorPos);
            if (slotIdx === null) return;
            slots = slots.slice(0, slotIdx) + key + slots.slice(slotIdx + 1);
            newCursorPos = slotToMaskPos(slotIdx + 1);
        } else if (key === 'Backspace') {
            const slotIdx = maskPosToSlotBackward(cursorPos);
            if (slotIdx === null) return;
            slots = slots.slice(0, slotIdx) + '_' + slots.slice(slotIdx + 1);
            newCursorPos = slotToMaskPos(slotIdx);
        } else if (key === 'Delete') {
            const slotIdx = maskPosToSlotForward(cursorPos);
            if (slotIdx === null) return;
            slots = slots.slice(0, slotIdx) + '_' + slots.slice(slotIdx + 1);
            newCursorPos = slotToMaskPos(slotIdx);
        } else {
            return;
        }

        setIsDirty(true);
        setMissingFromDate(false);
        applyFromDateSlots(slots);
        requestAnimationFrame(() => {
            if (fromDateInputRef.current) {
                fromDateInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        });
    };

    // Paste handler for from date (onChange only fires for paste, not typed keys)
    const handleFromDateChange = (e) => {
        if (!canEdit) return notifyReadOnly();
        const digits = extractDigits(e.target.value).slice(0, 8);
        const slots = (digits + '________').slice(0, 8);
        setIsDirty(true);
        setMissingFromDate(false);
        applyFromDateSlots(slots);
        const newCursorPos = digitsToCursorPos(digits.length);
        requestAnimationFrame(() => {
            if (fromDateInputRef.current) {
                fromDateInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        });
    };

    // Keystroke handler — cursor-aware insert/delete at any position
    const handleToDateKeyDown = (e) => {
        if (!canEdit) return notifyReadOnly();
        const input = toDateInputRef.current;
        if (!input) return;
        const { key, ctrlKey, metaKey } = e;
        if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) return;
        if (ctrlKey || metaKey) return;
        e.preventDefault();

        const cursorPos = input.selectionStart;
        let slots = toDateSlots;
        let newCursorPos = cursorPos;

        if (/^[0-9]$/.test(key)) {
            const slotIdx = maskPosToSlotForward(cursorPos);
            if (slotIdx === null) return;
            slots = slots.slice(0, slotIdx) + key + slots.slice(slotIdx + 1);
            newCursorPos = slotToMaskPos(slotIdx + 1);
        } else if (key === 'Backspace') {
            const slotIdx = maskPosToSlotBackward(cursorPos);
            if (slotIdx === null) return;
            slots = slots.slice(0, slotIdx) + '_' + slots.slice(slotIdx + 1);
            newCursorPos = slotToMaskPos(slotIdx);
        } else if (key === 'Delete') {
            const slotIdx = maskPosToSlotForward(cursorPos);
            if (slotIdx === null) return;
            slots = slots.slice(0, slotIdx) + '_' + slots.slice(slotIdx + 1);
            newCursorPos = slotToMaskPos(slotIdx);
        } else {
            return;
        }

        setIsDirty(true);
        setMissingToDate(false);
        applyToDateSlots(slots);
        requestAnimationFrame(() => {
            if (toDateInputRef.current) {
                toDateInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        });
    };

    // Paste handler for to date (onChange only fires for paste, not typed keys)
    const handleToDateChange = (e) => {
        if (!canEdit) return notifyReadOnly();
        const digits = extractDigits(e.target.value).slice(0, 8);
        const slots = (digits + '________').slice(0, 8);
        setIsDirty(true);
        setMissingToDate(false);
        applyToDateSlots(slots);
        const newCursorPos = digitsToCursorPos(digits.length);
        requestAnimationFrame(() => {
            if (toDateInputRef.current) {
                toDateInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        });
    };

    // Handle user blurring from date input
    const handleFromDateBlur = () => {
        const from = fromDateSlots.includes('_') ? '' : fromDateSlots;
        const to = toDateSlots.includes('_') ? '' : toDateSlots;
        validateDates("from", from, to, setFromDateError, setToDateError, setDurationInDays, setMissingFromDate, setMissingToDate);
    };

    // Handle user blurring to date input
    const handleToDateBlur = () => {
        const from = fromDateSlots.includes('_') ? '' : fromDateSlots;
        const to = toDateSlots.includes('_') ? '' : toDateSlots;
        validateDates("to", to, from, setFromDateError, setToDateError, setDurationInDays, setMissingFromDate, setMissingToDate);
    };

    // Build the from date from the mold 'slots' - DD/MM/YYYY
    const applyFromDateSlots = (slots) => {
        setFromDateSlots(slots);
        setFromDate(createMaskedDisplay(slots));
        const complete = !slots.includes('_');
        if (complete) {
            const { date, error } = buildDateFromDigits(slots);
            if (date) {
                setFromDateObj(date);
                setDurationInDays(calculateDurationFromDates(date, toDateObj));
                setFromDateError(false);
            } else {
                setFromDateError(error);
                setFromDateObj(null);
                setDurationInDays(null);
            }
        } else {
            setFromDateObj(null);
            setDurationInDays(null);
            setFromDateError(false);
        }
    };

    // Build the to date from the mold 'slots' - DD/MM/YYYY
    const applyToDateSlots = (slots) => {
        setToDateSlots(slots);
        setToDate(createMaskedDisplay(slots));
        const complete = !slots.includes('_');
        if (complete) {
            const { date, error } = buildDateFromDigits(slots);
            if (date) {
                setToDateObj(date);
                setDurationInDays(calculateDurationFromDates(fromDateObj, date));
                setToDateError(false);
            } else {
                setToDateError(error);
                setToDateObj(null);
                setDurationInDays(null);
            }
        } else {
            setToDateObj(null);
            setDurationInDays(null);
            setToDateError(false);
        }
    };

    // Close country dropdown on outside click
    useEffect(() => {
        if (!isCountryDropdownOpen) return;

        const handleClickOutside = (event) => {
            if (
                destinationWrapperRef.current &&
                !destinationWrapperRef.current.contains(event.target)
            ) {
                setIsCountryDropdownOpen(false);
                setCountryQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isCountryDropdownOpen]);

    useEffect(() => {
        if (fromDateObj) {
            const slots = formatDigits(fromDateObj);
            setFromDateSlots(slots);
            setFromDate(createMaskedDisplay(slots));
        }
    }, [fromDateObj])

    useEffect(() => {
        if (toDateObj) {
            const slots = formatDigits(toDateObj);
            setToDateSlots(slots);
            setToDate(createMaskedDisplay(slots));
        }
    }, [toDateObj])

    return (
        <>
            {/* Page header */}
            <section
                className={shared.heroHeader}
                ref={titleSectionRef}
            >
                <div className={styles.titleWrapper}>
                    {/* Error text above title */}
                    {titleError && (
                        <p className={styles.titleError}>
                            Enter your trip's title before saving.
                        </p>
                    )}

                    <div
                        className={styles.titleClickArea}
                        onClick={() => !isEditingTitle && startEditingTitle()}
                    >
                        {isEditingTitle ? (
                            <input
                                ref={titleInputRef}
                                type="text"
                                className={styles.titleInput}
                                value={title}
                                onChange={handleTitleChange}
                                onBlur={() => setIsEditingTitle(false)}
                                onKeyDown={handleTitleKeyDown}
                                placeholder="Enter Trip Title"
                                autoFocus
                            />
                        ) : (
                            <div>
                                <span
                                    className={`${styles.titleText}${title.trim() === '' ? ' ' + styles.placeholder : ''}`}>
                                    {title.trim() || "Enter Trip Title"}
                                </span>

                                {canEdit && (
                                    <img
                                        src={edit_icon}
                                        alt="Edit Icon"
                                        className={styles.titleEditIcon}
                                        onClick={() => setIsEditingTitle(true)}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.underlineTripTitle}></div>
                </div>
            </section>

            {/* Meta section */}
            <section className={styles.newTripMeta}>

                {/* Destination selection */}
                <div className={styles.fieldRow} style={{ position: 'relative', zIndex: 15000 }}>
                    <label
                        className={styles.fieldLabel}
                        htmlFor='destination-input'
                    >
                        Destination
                    </label>

                    <div className={styles.destinationOuterWrapper}>
                        {/* Input area */}
                        <div>
                            <div
                                className={styles.destinationInputWrapper}
                                ref={destinationWrapperRef}
                                onClick={openCountryDropdown}
                            >
                                <input
                                    id='destination-input'
                                    type="text"
                                    className={styles.destinationInput}
                                    autoComplete='off'
                                    // Let clicks pass through the disabled input to the wrapper (which toasts)
                                    style={canEdit ? undefined : { pointerEvents: 'none' }}
                                    ref={destinationInputRef}
                                    value={
                                        isCountryDropdownOpen
                                            ? countryQuery
                                            : destinationDisplay
                                    }
                                    placeholder={
                                        selectedCountries.length === 0
                                            ? "Select one or more countries..."
                                            : ""
                                    }
                                    onFocus={() => {
                                        setIsCountryDropdownOpen(true);
                                        setCountryQuery("");
                                    }}
                                    onChange={(e) => {
                                        setCountryQuery(e.target.value);
                                        setIsCountryDropdownOpen(true);
                                    }}
                                />

                                <img
                                    src={chevron_down}
                                    alt="Dropdown Icon"
                                    aria-hidden="true"
                                    aria-expanded={isCountryDropdownOpen}
                                    className={`${styles.destinationChevron} ${isCountryDropdownOpen ? styles.open : styles.close}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isCountryDropdownOpen) {
                                            setIsCountryDropdownOpen(false);
                                            setCountryQuery('');
                                        } else {
                                            openCountryDropdown();
                                        }
                                    }}
                                />

                                {isCountryDropdownOpen && (
                                    <ul className={styles.destinationDropdown}>
                                        {filteredCountries.length === 0 ? (
                                            <li className={`${styles.destinationOption} ${styles.destinationOptionEmpty}`}>
                                                No matches found
                                            </li>
                                        ) : (
                                            filteredCountries.map((country) => (
                                                <li
                                                    key={country}
                                                    className={`${styles.destinationOption} ${selectedCountries.includes(country) ? styles.destinationOptionSelected : ''}`}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleSelectCountry(country);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {/* Tick */}
                                                    <div className={`${styles.destinationCheckbox} ${selectedCountries.includes(country) ? styles.checked : ''}`}>
                                                        {selectedCountries.includes(country) && (
                                                            <img
                                                                src={tick_icon_white}
                                                                alt="Checked"
                                                                className={styles.destinationCheckboxIcon}
                                                            />
                                                        )}
                                                    </div>

                                                    {/* Country name */}
                                                    <span>{country}</span>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {missingDestination && (
                            <p className={styles.destinationErrorMessage}>
                                Please select at least one destination.
                            </p>
                        )}

                    </div>
                </div>

                {/* Date selection */}
                <div className={styles.newTripDatesRow}>
                    <label className={styles.newTripLabel}>Dates</label>

                    <div className={styles.newTripDatesInputs}>

                        {/* From date */}
                        <div className={styles.dateInputGroup}>
                            <div className={styles.dateSubLabel}>From</div>
                            <div className={styles.dateInputWrapper} onClick={() => { if (!canEdit) notifyReadOnly(); }}>
                                <input
                                    type="text"
                                    className={`${styles.dateInput} ${fromDateError ? styles.dateInputError : ""}`}
                                    placeholder="i.e. DD/MM/YYYY"
                                    inputMode="numeric"
                                    readOnly={!canEdit}
                                    ref={fromDateInputRef}
                                    value={fromDate}
                                    onKeyDown={handleFromDateKeyDown}
                                    onChange={handleFromDateChange}
                                    onBlur={handleFromDateBlur}
                                />
                                <DatePicker
                                    disabled={!canEdit}
                                    selected={fromDateObj}
                                    onChange={(date) => {
                                        setFromDateObj(date);
                                        setMissingFromDate(false);
                                        const slots = date ? formatDigits(date) : '________';
                                        setFromDateSlots(slots);
                                        setFromDate(date ? createMaskedDisplay(slots) : '');
                                        const to = toDateSlots.includes('_') ? '' : toDateSlots;
                                        validateDates("from", date ? slots : '', to, setFromDateError, setToDateError, setDurationInDays, setMissingFromDate, setMissingToDate);
                                        setIsDirty(true);
                                    }}
                                    customInput={
                                        <button
                                            type="button"
                                            className={styles.calendarButton}
                                            style={canEdit ? undefined : { pointerEvents: 'none' }}
                                        >
                                            <img
                                                src={calendar_icon}
                                                alt="Calendar Icon"
                                            />
                                        </button>
                                    }
                                    dateFormat="dd/MM/yyyy"
                                    wrapperClassName={styles.calendarWrapper}
                                    popperPlacement='bottom-end'
                                    popperModifiers={[
                                        offset({ mainAxis: 8, crossAxis: 0 })
                                    ]}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                            </div>

                            {fromDateError && (
                                <p className={styles.fromDateErrorMessage}>
                                    {fromDateError}
                                </p>
                            )}

                            {missingFromDate && (
                                <p className={styles.fromDateErrorMessage}>
                                    Please select a start date.
                                </p>
                            )}
                        </div>

                        {/* To date */}
                        <div className={styles.dateInputGroup}>
                            <div className={styles.dateSubLabel}>To</div>
                            <div className={styles.dateInputWrapper} onClick={() => { if (!canEdit) notifyReadOnly(); }}>
                                <input
                                    type="text"
                                    className={`${styles.dateInput} ${toDateError ? styles.dateInputError : ""}`}
                                    placeholder="i.e. DD/MM/YYYY"
                                    inputMode="numeric"
                                    readOnly={!canEdit}
                                    ref={toDateInputRef}
                                    value={toDate}
                                    onKeyDown={handleToDateKeyDown}
                                    onChange={handleToDateChange}
                                    onBlur={handleToDateBlur}
                                />
                                <DatePicker
                                    disabled={!canEdit}
                                    selected={toDateObj}
                                    onChange={(date) => {
                                        setToDateObj(date);
                                        setMissingToDate(false);
                                        const slots = date ? formatDigits(date) : '________';
                                        setToDateSlots(slots);
                                        setToDate(date ? createMaskedDisplay(slots) : '');
                                        const from = fromDateSlots.includes('_') ? '' : fromDateSlots;
                                        validateDates("to", date ? slots : '', from, setFromDateError, setToDateError, setDurationInDays, setMissingFromDate, setMissingToDate);
                                        setIsDirty(true);
                                    }}
                                    customInput={
                                        <button
                                            type="button"
                                            className={styles.calendarButton}
                                            style={canEdit ? undefined : { pointerEvents: 'none' }}
                                        >
                                            <img
                                                src={calendar_icon}
                                                alt="Calendar Icon"
                                            />
                                        </button>
                                    }
                                    dateFormat="dd/MM/yyyy"
                                    wrapperClassName={styles.calendarWrapper}
                                    popperPlacement='bottom-end'
                                    popperModifiers={[
                                        offset({ mainAxis: 8, crossAxis: 0 })
                                    ]}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                                {/* Duration — absolutely placed beside the To input so it never shrinks the box */}
                                {durationInDays !== null &&
                                    <p className={styles.tripDurationText}>
                                        ({durationInDays} day{durationInDays !== 1 ? 's' : ''})
                                    </p>
                                }
                            </div>

                            {toDateError && (
                                <p className={styles.toDateErrorMessage}>
                                    {toDateError}
                                </p>
                            )}

                            {missingToDate && (
                                <p className={styles.toDateErrorMessage}>
                                    Please select an end date.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default TripHeader;